import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Chip, InputAdornment, MenuItem, Pagination, Paper, TextField, Typography
} from '@mui/material';
import {
  HistoryOutlined, SearchOutlined, SortOutlined
} from '@mui/icons-material';
import { apiFetch } from '../services/api';

const card={
  border:'1px solid #e3eaf3',
  boxShadow:'0 8px 26px rgba(18,46,84,.055)',
  borderRadius:3,
  bgcolor:'#fff'
};

const akcjePL={
  CREATE:'Utworzono',
  UPDATE:'Edytowano',
  DELETE:'Usunięto',
  DELETE_REQUEST:'Wniosek o usunięcie',
  DELETE_REJECT:'Odrzucono usunięcie',
  DELETE_APPROVE:'Zatwierdzono usunięcie',
  MOVE:'Przesunięto',
  LOGIN:'Logowanie',
  EXPORT:'Eksport raportu',
  ACCOUNT_CREATE:'Utworzono konto',
  ACCOUNT_UPDATE:'Edytowano konto',
  ACCOUNT_DELETE:'Usunięto konto',
  PASSWORD_RESET_REQUEST:'Reset hasła',
  PASSWORD_RESET:'Zmieniono hasło',
  PASSWORD_CHANGE_FORCE:'Wymuszono zmianę hasła',
  PASSWORD_CHANGE_REQUIRED:'Wymagana zmiana hasła',
  FIRST_PASSWORD_CHANGE:'Pierwsza zmiana hasła',
  DEPRECIATION_CREATE:'Utworzono amortyzację',
  DEPRECIATION_UPDATE:'Zmieniono amortyzację'
};

const encjePL={
  srodek_trwaly:'Środek trwały',
  konto:'Konto użytkownika',
  lokalizacja:'Lokalizacja',
  pracownik:'Pracownik',
  producent:'Producent',
  firma_serwisowa:'Firma serwisowa',
  wniosek_usuniecia:'Wniosek o usunięcie',
  raport:'Raport'
};

const akcjaLabel=a=>akcjePL[a]||String(a||'Inna operacja').replaceAll('_',' ').toLowerCase();
const encjaLabel=e=>encjePL[e]||String(e||'System').replaceAll('_',' ');

function opisLogu(x){
  const opis=String(x.opis||'').trim();
  const encja=encjaLabel(x.encja);
  const id=x.id_encji ? ` #${x.id_encji}` : '';

  const srodek=[x.srodek_nazwa,x.srodek_model].filter(Boolean).join(' — ');
  if(x.encja==='srodek_trwaly' && srodek){
    const szczegoly=[x.srodek_inw&&`INW: ${x.srodek_inw}`,x.srodek_sn&&`SN: ${x.srodek_sn}`].filter(Boolean).join(' | ');
    if(!opis || ['Dodano środek trwały','Edytowano środek trwały','Przesunięcie środka'].includes(opis)){
      return `${akcjaLabel(x.akcja)}: ${srodek}${szczegoly?` | ${szczegoly}`:''}`;
    }
  }

  if(x.encja==='konto' && x.konto_login && (!opis || opis==='Edytowano konto użytkownika')){
    return `${akcjaLabel(x.akcja)}: ${x.konto_login}${x.konto_nazwa?` — ${x.konto_nazwa}`:''}${x.konto_email?` | ${x.konto_email}`:''}`;
  }

  if(opis) return opis;
  return `${akcjaLabel(x.akcja)} — ${encja}${id}`;
}

function chipStyle(akcja){
  if(['DELETE','DELETE_APPROVE','ACCOUNT_DELETE'].includes(akcja)) return {bg:'#fdecec',fg:'#b42318'};
  if(['CREATE','ACCOUNT_CREATE'].includes(akcja)) return {bg:'#e8f7ed',fg:'#237a3b'};
  if(['UPDATE','MOVE','ACCOUNT_UPDATE'].includes(akcja)) return {bg:'#eef4ff',fg:'#2364d8'};
  if(['LOGIN','EXPORT'].includes(akcja)) return {bg:'#f1f3f6',fg:'#4b5a6a'};
  return {bg:'#fff4dd',fg:'#9a6700'};
}

export default function Logi() {
  const [dane,setDane]=useState([]);
  const [blad,setBlad]=useState('');
  const [szukaj,setSzukaj]=useState('');
  const [filtrAkcji,setFiltrAkcji]=useState('WSZYSTKIE');
  const [sort,setSort]=useState('NAJNOWSZE');
  const [strona,setStrona]=useState(1);
  const NA_STRONIE=20;

  useEffect(()=>{
    apiFetch('/logi?limit=500')
      .then(r=>setDane(Array.isArray(r?.dane)?r.dane:[]))
      .catch(e=>setBlad(e.message));
  },[]);

  const typyAkcji=useMemo(()=>Array.from(new Set(dane.map(x=>x.akcja).filter(Boolean))).sort(),[dane]);

  const meta=useMemo(()=>{
    const q=szukaj.trim().toLowerCase();
    let wynik=[...dane];

    if(filtrAkcji!=='WSZYSTKIE') wynik=wynik.filter(x=>x.akcja===filtrAkcji);
    if(q) wynik=wynik.filter(x=>[
      x.uzytkownik,akcjaLabel(x.akcja),x.akcja,encjaLabel(x.encja),x.encja,x.id_encji,opisLogu(x),x.ip
    ].some(v=>String(v??'').toLowerCase().includes(q)));

    wynik.sort((a,b)=>sort==='NAJSTARSZE'
      ? new Date(a.utworzono)-new Date(b.utworzono)
      : new Date(b.utworzono)-new Date(a.utworzono));

    const strony=Math.max(1,Math.ceil(wynik.length/NA_STRONIE));
    const bezpieczna=Math.min(strona,strony);
    return {
      dane:wynik.slice((bezpieczna-1)*NA_STRONIE,bezpieczna*NA_STRONIE),
      liczba:wynik.length,strony,bezpieczna
    };
  },[dane,szukaj,filtrAkcji,sort,strona]);

  return <Box>
    <Box sx={{display:'flex',alignItems:'flex-start',gap:1.2,mb:2}}>
      <Box sx={{width:42,height:42,borderRadius:2,bgcolor:'#eef4ff',color:'#2364d8',display:'grid',placeItems:'center'}}>
        <HistoryOutlined/>
      </Box>
      <Box>
        <Typography variant="h6" fontWeight={900}>Logi</Typography>
        <Typography variant="body2" color="text.secondary">Historia najważniejszych operacji wykonanych w systemie.</Typography>
      </Box>
    </Box>

    {blad&&<Paper sx={{...card,p:2,mb:2}}><Typography color="error">{blad}</Typography></Paper>}

    <Paper sx={{...card,p:1.5,mb:2}}>
      <Box sx={{display:'flex',gap:1.2,justifyContent:'space-between',alignItems:'center',flexWrap:'wrap'}}>
        <TextField size="small" placeholder="Szukaj po użytkowniku, akcji, obiekcie, opisie lub ID..." value={szukaj}
          onChange={e=>{setSzukaj(e.target.value);setStrona(1);}} sx={{minWidth:{xs:'100%',sm:390}}}
          InputProps={{startAdornment:<InputAdornment position="start"><SearchOutlined fontSize="small"/></InputAdornment>}}/>
        <Box sx={{display:'flex',gap:1,flexWrap:'wrap'}}>
          <TextField select size="small" label="Rodzaj operacji" value={filtrAkcji} onChange={e=>{setFiltrAkcji(e.target.value);setStrona(1);}} sx={{minWidth:210}}>
            <MenuItem value="WSZYSTKIE">Wszystkie operacje</MenuItem>
            {typyAkcji.map(a=><MenuItem key={a} value={a}>{akcjaLabel(a)}</MenuItem>)}
          </TextField>
          <TextField select size="small" value={sort} onChange={e=>setSort(e.target.value)} sx={{minWidth:170}}
            InputProps={{startAdornment:<InputAdornment position="start"><SortOutlined fontSize="small"/></InputAdornment>}}>
            <MenuItem value="NAJNOWSZE">Najnowsze</MenuItem>
            <MenuItem value="NAJSTARSZE">Najstarsze</MenuItem>
          </TextField>
        </Box>
      </Box>
    </Paper>

    <Paper sx={{...card,p:2,overflowX:'auto'}}>
      <Box sx={{minWidth:1000}}>
        <Box sx={{display:'grid',gridTemplateColumns:'155px 1.15fr 1.05fr 1fr 2.5fr 110px',gap:1.4,px:1,py:1,bgcolor:'#f8fafc',borderRadius:1.5,fontWeight:850,fontSize:13}}>
          <span>Data</span><span>Użytkownik</span><span>Operacja</span><span>Obiekt</span><span>Szczegóły</span><span>IP</span>
        </Box>

        {meta.dane.length===0?<Typography color="text.secondary" sx={{py:5,textAlign:'center'}}>Brak logów spełniających wybrane kryteria.</Typography>:
          meta.dane.map(x=>{const sc=chipStyle(x.akcja);return <Box key={x.id_logu} sx={{display:'grid',gridTemplateColumns:'155px 1.15fr 1.05fr 1fr 2.5fr 110px',gap:1.4,px:1,py:1.45,borderBottom:'1px solid #edf1f6',alignItems:'center'}}>
            <Typography variant="caption" color="text.secondary">{new Date(x.utworzono).toLocaleString('pl-PL')}</Typography>
            <Typography variant="body2" fontWeight={800}>{x.uzytkownik||'System'}</Typography>
            <Chip size="small" label={akcjaLabel(x.akcja)} sx={{justifySelf:'start',bgcolor:sc.bg,color:sc.fg,fontWeight:800}}/>
            <Box><Typography variant="body2" fontWeight={800}>{encjaLabel(x.encja)}</Typography>{x.id_encji&&<Typography variant="caption" color="text.secondary">ID: {x.id_encji}</Typography>}</Box>
            <Typography variant="body2">{opisLogu(x)}</Typography>
            <Typography variant="caption" color="text.secondary">{x.ip||'—'}</Typography>
          </Box>;})
        }

        {meta.liczba>0&&<Box sx={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:2,pt:2,flexWrap:'wrap'}}>
          <Typography variant="caption" color="text.secondary">Wyniki {((meta.bezpieczna-1)*NA_STRONIE)+1}–{Math.min(meta.bezpieczna*NA_STRONIE,meta.liczba)} z {meta.liczba}</Typography>
          <Pagination count={meta.strony} page={meta.bezpieczna} onChange={(_,p)=>setStrona(p)} color="primary" size="small"/>
        </Box>}
      </Box>
    </Paper>
  </Box>;
}
